import { useCallback, useEffect, useState } from 'react'

import { RequestStatus } from 'core/domain/Request'
import { useAbortController } from 'core/ui/hooks/useAbortController'
import { downloadAttachment as downloadAttachmentUseCase } from 'core/use-cases/downloadAttachment'

import { sentry } from 'legacy/utils/sentry'

import { type Load } from '../domain/Load'
import { getTender as getTenderUseCase } from '../use-cases/getTender'

type TenderState = {
  load?: Load
  status: RequestStatus
  error?: Error
}

type AttachmentState = {
  statusAttachment?: RequestStatus
  attachmentUrl?: string
}

function getTenderInitialState(): TenderState {
  return {
    status: RequestStatus.Initial,
  }
}

function getAttachmentInitialState(): AttachmentState {
  return {
    statusAttachment: RequestStatus.Initial,
  }
}

export function useTender(id: string) {
  const abortController = useAbortController([id])
  const [{ load, status, error }, setTenderState] = useState<TenderState>(getTenderInitialState)
  const [{ statusAttachment }, setAttachmentState] =
    useState<AttachmentState>(getAttachmentInitialState)

  const fetchLoad = useCallback(async () => {
    try {
      setTenderState({ ...getTenderInitialState(), status: RequestStatus.Pending })

      const result = await getTenderUseCase(id, abortController)

      setTenderState({ load: result, status: RequestStatus.Done })
    } catch (requestError) {
      if (!abortController.signal.aborted) {
        setTenderState({
          load: undefined,
          status: RequestStatus.Error,
          error: requestError as Error,
        })
        sentry.log(requestError)
      }
    }
  }, [id, abortController])

  const downloadAttachment = useCallback(
    async (attachmentId: number) => {
      try {
        setAttachmentState(previous => ({ ...previous, statusAttachment: RequestStatus.Pending }))

        await downloadAttachmentUseCase(
          { rateUuid: load!.rateUUID!, quoteUuid: load!.quoteUUID!, attachmentId },
          abortController
        )

        setAttachmentState(previous => ({
          ...previous,
          statusAttachment: RequestStatus.Done,
        }))
      } catch (requestError) {
        if (!abortController.signal.aborted) {
          sentry.log(requestError)
        }
      } finally {
        setAttachmentState(previous => ({ ...previous, statusAttachment: RequestStatus.Done }))
      }
    },
    [load, abortController]
  )

  useEffect(() => {
    fetchLoad()
  }, [fetchLoad])

  return {
    fetchLoad,
    load,
    status,
    error,
    statusAttachment,
    downloadAttachment,
  }
}
